import { createApi } from '@reduxjs/toolkit/query/react';

import type {
  MiniTortClaimDetail,
  MiniTortClaimFlag,
  MiniTortClaimListParams,
  MiniTortClaimListResponse,
  MiniTortClaimUpdatePayload,
  MiniTortTimelineEntry,
  PipelineStatus,
  ResolveFlagPayload,
  TransitionPayload,
} from '@/components/mini-tort/types';

import { baseQueryWithErrorHandling } from './baseQuery';

export const miniTortApiClient = createApi({
  reducerPath: 'miniTortApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['MiniTortClaims', 'MiniTortClaim', 'MiniTortFlags', 'MiniTortStatus', 'MiniTortTimeline'],
  endpoints: (builder) => ({
    listClaims: builder.query<MiniTortClaimListResponse, MiniTortClaimListParams>({
      query: (params) => ({
        url: '/api/mini-tort/claims/',
        params: {
          ...(params.stage && { stage: params.stage }),
          ...(params.injury_detected !== undefined && { injury_detected: params.injury_detected }),
          ...(params.ordering && { ordering: params.ordering }),
          limit: params.limit ?? 50,
          offset: params.offset ?? 0,
        },
      }),
      providesTags: ['MiniTortClaims'],
    }),

    getClaimDetail: builder.query<MiniTortClaimDetail, string>({
      query: (id) => `/api/mini-tort/claims/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'MiniTortClaim', id }],
    }),

    updateClaim: builder.mutation<MiniTortClaimDetail, { id: string; body: MiniTortClaimUpdatePayload }>({
      query: ({ id, body }) => ({
        url: `/api/mini-tort/claims/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'MiniTortClaim', id },
        'MiniTortClaims',
        { type: 'MiniTortStatus', id },
      ],
    }),

    transitionStage: builder.mutation<MiniTortClaimDetail, { id: string; body: TransitionPayload }>({
      query: ({ id, body }) => ({
        url: `/api/mini-tort/claims/${id}/transition/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'MiniTortClaim', id },
        'MiniTortClaims',
        { type: 'MiniTortStatus', id },
        { type: 'MiniTortTimeline', id },
      ],
    }),

    getClaimFlags: builder.query<MiniTortClaimFlag[], string>({
      query: (id) => `/api/mini-tort/claims/${id}/flags/`,
      providesTags: (_result, _error, id) => [{ type: 'MiniTortFlags', id }],
    }),

    resolveFlag: builder.mutation<MiniTortClaimFlag, { claimId: string; flagId: string; body: ResolveFlagPayload }>({
      query: ({ claimId, flagId, body }) => ({
        url: `/api/mini-tort/claims/${claimId}/flags/${flagId}/resolve/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { claimId }) => [
        { type: 'MiniTortFlags', id: claimId },
        { type: 'MiniTortClaim', id: claimId },
        { type: 'MiniTortStatus', id: claimId },
      ],
    }),

    getClaimStatus: builder.query<PipelineStatus, string>({
      query: (id) => `/api/mini-tort/claims/${id}/status/`,
      providesTags: (_result, _error, id) => [{ type: 'MiniTortStatus', id }],
    }),

    getClaimTimeline: builder.query<MiniTortTimelineEntry[], string>({
      query: (id) => `/api/mini-tort/claims/${id}/timeline/`,
      providesTags: (_result, _error, id) => [{ type: 'MiniTortTimeline', id }],
    }),

    holdAutoClose: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/mini-tort/claims/${id}/auto-close/hold/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'MiniTortStatus', id },
        { type: 'MiniTortClaim', id },
      ],
    }),

    resumeAutoClose: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/mini-tort/claims/${id}/auto-close/resume/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'MiniTortStatus', id },
        { type: 'MiniTortClaim', id },
      ],
    }),
  }),
});

export const {
  useListClaimsQuery,
  useGetClaimDetailQuery,
  useUpdateClaimMutation,
  useTransitionStageMutation,
  useGetClaimFlagsQuery,
  useResolveFlagMutation,
  useGetClaimStatusQuery,
  useGetClaimTimelineQuery,
  useHoldAutoCloseMutation,
  useResumeAutoCloseMutation,
} = miniTortApiClient;
